import { fetchAeropayUser } from '@iheartjane/data-access';
import { FLAGS } from '@iheartjane/feature-flags';
import {
  getApplicableStoreSpecialIds,
  shouldShowGold as showGold,
} from '@iheartjane/menu';
import { useEffect, useRef } from 'react';
import type {
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from 'react-router';
import { useLoaderData } from 'react-router';

import {
  PRODUCT_REVIEWS_PAGE_SIZE,
  buildEmptyProductReviewsResponse,
  getProductReviews,
  getProductReviewsAggregate,
  normalizeProductReviewsPage,
} from '@jane/bloom/dataAccess/dm/productReviews.server';
import { reviewSortFromParam } from '@jane/bloom/dataAccess/dm/productReviewsSort';
import {
  fetchBrandSpecials,
  fetchBrandSpecialsQualifyingStatuses,
} from '@jane/bloom/dataAccess/marketplace/brandSpecials.server';
import {
  fetchAllStoreSpecials,
  fetchStoreSpecialsQualifyingStatuses,
} from '@jane/bloom/dataAccess/marketplace/specials.server';
import { useStoreSwitcher } from '@jane/bloom/hooks/storeSwitcher/useStoreSwitcher';
import { globalSubloader } from '@jane/bloom/loaders/globalSubloader.server';
import { JANE_GOLD_CONFIG_CONTEXT } from '@jane/bloom/middlewares/janeGoldConfigContext.server';
import { MENU_CONTEXT } from '@jane/bloom/middlewares/menuContext.server';
import { breadcrumbList } from '@jane/bloom/resources/meta/breadcrumbList';
import { canonicalTag } from '@jane/bloom/resources/meta/canonicalTag';
import { createMetaVariables } from '@jane/bloom/resources/meta/createMetaVariables';
import { getMetaDescription } from '@jane/bloom/resources/meta/description';
import { getRootLoaderData } from '@jane/bloom/resources/meta/getRootLoaderData';
import { openGraph } from '@jane/bloom/resources/meta/openGraph';
import { parentMeta } from '@jane/bloom/resources/meta/parentMeta';
import { preloadImage } from '@jane/bloom/resources/meta/preloadImage';
import { productStructuredData } from '@jane/bloom/resources/meta/product';
import { getMetaTitle } from '@jane/bloom/resources/meta/title';
import { trackViewedProduct } from '@jane/bloom/services/tracking/track';
import { getCartInfo } from '@jane/bloom/sessions/cart.server';
import { getUserIdentity } from '@jane/bloom/sessions/userIdentity.server';
import type { RouteHandle } from '@jane/bloom/types/routeHandle';
import type { StoreSpecials } from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import { getDefaultCart } from '@jane/bloom/utils/cart/getDefaultCart.server';
import { initializeDMContext } from '@jane/bloom/utils/dm/dmContext.server';
import { isFeatureFlagsEnabled } from '@jane/bloom/utils/isFeatureFlagEnabled.server';
import { logError } from '@jane/bloom/utils/logError.server';
import { redirectToParentNotFound } from '@jane/bloom/utils/redirectToParentNotFound.server';
import { promiseHashAllSettled } from '@jane/bloom/utils/requests/promiseHashAllSettled';
import { getCustomLanguage } from '@jane/bloom/viewModels/getCustomLanguage.server';
import { fetchMenuProduct } from '@jane/bloom/viewModels/menuProduct.server';
import { buildProductStoreViewModel } from '@jane/bloom/viewModels/productDetail';
import { buildProductReviewsViewModel } from '@jane/bloom/viewModels/productReviews';
import { buildRecommendedPDPRowViewModel } from '@jane/bloom/viewModels/recommendedPDPRow.server';

import { breadcrumbs } from './components/breadcrumbs';
import { DMProductDetailsRow } from './components/dmPDPRow';
import { ProductDetailCard } from './components/productDetailCard';
import { ProductReviewsSection } from './components/productDetailCard/productReviews/productReviewsSection';
import type { LoaderResponse } from './types';

import stylesLink from './styles.css?url';
import desktopStylesLink from './styles.desktop.css?url';

export const handle: RouteHandle = {
  hydrate: true,
};

export const links: LinksFunction = () => [
  { href: stylesLink, rel: 'stylesheet' },
  { href: desktopStylesLink, media: '(min-width: 767px)', rel: 'stylesheet' },
];

export const meta: MetaFunction<typeof loader> = (args) => {
  const { customSEO, menuUrl, metaProps, pinnedStore } =
    getRootLoaderData(args);
  const data = args.loaderData;

  const pdpViewModel = data?.productDetail;
  const image = pdpViewModel?.productImagesSrcSets?.[0] ?? null;

  // Match metaProps to available customSEO variables
  const metaVariables = createMetaVariables(metaProps, {
    productBrand: pdpViewModel?.product.brand,
    productCategory: pdpViewModel?.product.kind,
    productLineage: pdpViewModel?.product.category,
    productName: pdpViewModel?.product.name,
    productSubCategory: pdpViewModel?.product.root_subtype,
  });

  return [
    getMetaTitle(customSEO?.pdp, metaVariables),
    getMetaDescription(customSEO?.pdp, metaVariables),
    canonicalTag(args.location, menuUrl),
    productStructuredData(pdpViewModel?.product, pinnedStore.currency_code),
    breadcrumbList(data?.breadcrumbs, menuUrl),
    openGraph(pdpViewModel?.product),
    preloadImage({
      sizes: image?.sizes,
      src: image?.fallbackSrc,
      srcSet: image?.srcSet,
    }),
    ...parentMeta(args),
  ];
};

export const loader = async ({
  context,
  params,
  request,
  url,
}: LoaderFunctionArgs) => {
  const { menu } = context.get(MENU_CONTEXT);
  const productId = params['productId'];
  if (!productId) {
    logError(`productId missing: ${productId}`);
    throw redirectToParentNotFound({ menuUrl: menu.menu_url, request });
  }

  const { goldConfig } = context.get(JANE_GOLD_CONFIG_CONTEXT);
  const { pinnedStore, shoppingPreferences } = await globalSubloader(
    request,
    menu
  );

  const searchParams = url.searchParams;
  const reviewsSort = reviewSortFromParam(searchParams.get('reviewsSort'));
  const reviewsPage = normalizeProductReviewsPage(
    searchParams.get('reviewsPage')
  );

  const cookie = request.headers.get('Cookie');

  const dmContextPromise = initializeDMContext({ context, request });

  // Kick off independent requests immediately (not used until final response)
  const brandSpecialsPromise = fetchBrandSpecials({ storeId: pinnedStore.id });
  // List shown on the PDP: only reviews WITH a comment (the SEO content).
  const productReviewsPromise = pinnedStore.product_reviews_enabled
    ? dmContextPromise.then(({ sdk }) =>
        getProductReviews({
          hasComment: true,
          page: reviewsPage,
          pageSize: PRODUCT_REVIEWS_PAGE_SIZE,
          productId: Number(productId),
          sdk,
          sort: reviewsSort,
        })
      )
    : Promise.resolve(buildEmptyProductReviewsResponse(Number(productId)));

  // Facets (feelings/activities/rating distribution) are computed over ALL
  // reviews via a separate, unfiltered aggregate fetch. Cheap and cached.
  const productReviewsFacetsPromise = pinnedStore.product_reviews_enabled
    ? dmContextPromise.then(({ sdk }) =>
        getProductReviewsAggregate({ productId: Number(productId), sdk })
      )
    : Promise.resolve(
        buildEmptyProductReviewsResponse(Number(productId)).aggregate
      );

  // Wave 1: Fetch critical dependencies needed for validation and Wave 2
  const { cart, menuProduct, userIdentity } = await promiseHashAllSettled({
    cart: {
      defaultValue: getDefaultCart(),
      promise: getCartInfo(cookie, menu.cookie_prefix),
    },
    menuProduct: {
      defaultValue: {
        isAvailableInTheArea: undefined,
        isOutOfStock: undefined,
        product: null,
        storesCarryingProduct: [],
      },
      promise: fetchMenuProduct({
        productId,
        shoppingPreferences,
        stores: menu.stores,
      }),
    },
    userIdentity: {
      defaultValue: undefined,
      promise: getUserIdentity(request, menu.cookie_prefix),
    },
  });

  const { isAvailableInTheArea, isOutOfStock, product, storesCarryingProduct } =
    menuProduct;

  if (!product) {
    throw redirectToParentNotFound({ menuUrl: menu.menu_url, request });
  }

  const { jdid, sdk } = await dmContextPromise;

  const productDetail = buildProductStoreViewModel({
    customLanguage: getCustomLanguage(pinnedStore),
    pinnedStore,
    product,
    storesCarryingProduct,
  });

  const featureFlags = await isFeatureFlagsEnabled({
    flagNames: [FLAGS.c3SpecialsRedesignMenu, FLAGS.c3UseV3StoreSpecials],
    jdid,
    request,
  });
  const useV3StoreSpecials = !!featureFlags[FLAGS.c3UseV3StoreSpecials];

  const applicableStoreSpecialIds = featureFlags[FLAGS.c3SpecialsRedesignMenu]
    ? getApplicableStoreSpecialIds(product)
    : new Set([]);

  // Resolve store specials early so we can filter by special_type
  const storeSpecials: StoreSpecials = await fetchAllStoreSpecials(
    shoppingPreferences.storeId,
    { sort: 'schedule', useV3: useV3StoreSpecials }
  );

  // Wave 2: Await independent requests + fetch queries that depend on Wave 1 results
  const {
    aeropayUser,
    brandSpecials,
    brandSpecialsQualifyingStatuses,
    productReviewsData,
    productReviewsFacetsAggregate,
    recommendedPDPRowViewModel,
    storeSpecialsQualifyingStatuses,
  } = await promiseHashAllSettled({
    aeropayUser: {
      defaultValue: null,
      promise: fetchAeropayUser({
        jwt: userIdentity?.jwtToken,
      }),
    },
    brandSpecials: {
      defaultValue: [],
      promise: brandSpecialsPromise,
    },
    brandSpecialsQualifyingStatuses: {
      defaultValue: {},
      promise: fetchBrandSpecialsQualifyingStatuses({
        brandSpecialIds: product.applicable_brand_special_ids,
        cartUuid: cart.uuid,
        storeId: pinnedStore.id,
      }),
    },
    productReviewsData: {
      defaultValue: buildEmptyProductReviewsResponse(Number(productId)),
      promise: productReviewsPromise,
    },
    productReviewsFacetsAggregate: {
      defaultValue: buildEmptyProductReviewsResponse(Number(productId))
        .aggregate,
      promise: productReviewsFacetsPromise,
    },
    recommendedPDPRowViewModel: {
      defaultValue: {
        cart,
        customLanguage: getCustomLanguage(pinnedStore),
        row: undefined,
      },
      promise: buildRecommendedPDPRowViewModel({
        cart,
        isAvailableInTheArea,
        product,
        sdk,
        store: pinnedStore,
      }),
    },
    storeSpecialsQualifyingStatuses: {
      defaultValue: {},
      promise: fetchStoreSpecialsQualifyingStatuses({
        cartUuid: cart.uuid || '0',
        specialIds: applicableStoreSpecialIds,
        storeId: pinnedStore.id,
        storeSpecials,
      }),
    },
  });

  // Build product reviews view model from fetched data
  const productReviews = buildProductReviewsViewModel({
    aggregateRating: product.aggregate_rating,
    facetsAggregate: productReviewsFacetsAggregate,
    pinnedStore,
    sdkResponse: productReviewsData,
    totalReviewCount: product.review_count ?? 0,
  });

  const shouldShowGold = showGold({
    appMode: 'bloom',
    goldConfig,
    state: pinnedStore.state,
    storeId: pinnedStore.id,
  });

  const isGoldUser = shouldShowGold && aeropayUser?.user_id;

  const loaderData: LoaderResponse = {
    applicableStoreSpecialIds,
    brandSpecials,
    brandSpecialsQualifyingStatuses: brandSpecialsQualifyingStatuses ?? {},
    breadcrumbs: breadcrumbs({
      category: productDetail.product.kind,
      name: productDetail.product.name,
    }),
    isAvailableInTheArea,
    isGoldUser: !!isGoldUser,
    isOutOfStock,
    isSignedIn: !!userIdentity,
    jdid,
    productDetail,
    productReviews,
    recommendedPDPRowViewModel,
    shouldShowGold,
    storeSpecials,
    storeSpecialsQualifyingStatuses: storeSpecialsQualifyingStatuses ?? {},
  };

  return loaderData;
};

export default function ProductDetailRoute() {
  const {
    applicableStoreSpecialIds,
    brandSpecials,
    brandSpecialsQualifyingStatuses,
    breadcrumbs,
    isAvailableInTheArea,
    isGoldUser,
    isOutOfStock,
    isSignedIn,
    productDetail,
    productReviews,
    recommendedPDPRowViewModel,
    shouldShowGold,
    storeSpecials,
    storeSpecialsQualifyingStatuses,
  } = useLoaderData<typeof loader>();
  const { product } = productDetail;

  const { hasPinnedStoreId, pinning } = useStoreSwitcher();
  const reviewSectionRef = useRef<HTMLDivElement>(null);

  const scrollToReviewSection = () => {
    reviewSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    trackViewedProduct({
      isOutOfStock: !!isOutOfStock,
      product,
    });
  }, [isOutOfStock, product]);

  return (
    <main className="pdp-container-main">
      <ProductDetailCard
        applicableStoreSpecialIds={applicableStoreSpecialIds}
        brandSpecials={brandSpecials}
        brandSpecialsQualifyingStatuses={brandSpecialsQualifyingStatuses}
        breadcrumbs={breadcrumbs}
        isAvailableInTheArea={isAvailableInTheArea}
        isOutOfStock={isOutOfStock}
        isStoreChanging={pinning || !hasPinnedStoreId}
        productDetail={productDetail}
        productReviewsSummary={productReviews.summary}
        scrollToReviewSection={scrollToReviewSection}
        shouldShowGold={shouldShowGold}
        isGoldUser={isGoldUser}
        isSignedIn={isSignedIn}
        storeSpecials={storeSpecials}
        storeSpecialsQualifyingStatuses={storeSpecialsQualifyingStatuses}
      />

      <DMProductDetailsRow
        brandSpecials={brandSpecials}
        recommendedPDPRow={recommendedPDPRowViewModel.row}
        cart={recommendedPDPRowViewModel.cart}
        customLanguage={recommendedPDPRowViewModel.customLanguage}
        specials={storeSpecials}
        shouldShowGold={shouldShowGold}
      />

      <ProductReviewsSection
        ref={reviewSectionRef}
        productIdentifiers={{
          id: product.product_id,
          slug: product.unique_slug ?? '',
        }}
        productReviewsViewModel={productReviews}
      />
    </main>
  );
}
